import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { compose } from 'redux';
import { reduxForm, Field, formValueSelector } from 'redux-form/immutable';
import { connect } from 'react-redux';

import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextFieldForm from 'components/mui/Form/TextField/TextFieldForm';
import UploadImg from './components/UploadImg';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import elements from 'components/elements';
import SelectFormLegacy from 'components/mui/Form/Select/SelectFormLegacy';

import { listToSelectOptions } from 'lib/helpers';
import { countries, countriesWithExtraInfo } from 'lib/countries';
import { renderDivider, renderOption } from 'pages/common/forms/helpers/selectOptions';
import { organizationCountrySelector } from 'domain/organization';
import {
  ACCEPTED_FILE_TYPES,
  createCompanyValidate,
} from 'pages/companies/components/CreateCompany/components/CreateCompanyForm/validation';

const CreateCompanyForm = ({ handleSubmit, picture, formID, submitting, onClose, country, invalid }) => {
  const { formatMessage } = useIntl();

  const countriesOptions = useCallback(() => {
    const options = listToSelectOptions(countries);
    return [...options.splice(0, 7).map(renderOption), renderDivider(), ...options.splice(7).map(renderOption)];
  }, []);

  const isRequiredExtraData = useMemo(() => {
    return countriesWithExtraInfo.includes(country);
  }, [country]);

  return (
    <form onSubmit={handleSubmit} id={formID}>
      <DialogContent>
        <Grid container alignItems="center" justifyContent="center" width="100%" mb={2.5}>
          <Field
            component={UploadImg}
            label={{
              id: 'form.company.create.logo.label',
              defaultMessage: 'Company logo',
            }}
            mark="logo.png"
            accept={ACCEPTED_FILE_TYPES.join(',')}
            capital
            name="picture"
            src={picture ? window.URL.createObjectURL(picture) : null}
          />
        </Grid>
        <Typography variant="body2" mb={2.5} fontSize={12}>
          {formatMessage({
            id: 'form.company.create.inputs.label',
            defaultMessage: 'Company information',
          })}
        </Typography>

        <Box display="flex" flexDirection="column" gap={2.5} width="100%">
          <Field
            component={TextFieldForm}
            props={{
              label: formatMessage({
                id: 'form.company.create.name.label',
                defaultMessage: 'Company name',
              }),
              placeholder: formatMessage({
                id: 'form.company.create.name.placeholder',
                defaultMessage: 'Company name',
              }),
              slotProps: {
                htmlInput: {
                  maxLength: 64,
                },
              },
              size: 'small',
              fullWidth: true,
              withTrim: true,
            }}
            required
            name="name"
          />

          <Field
            component={SelectFormLegacy}
            renderOptions={countriesOptions}
            label={`${formatMessage({
              id: 'forms.common.main.country.label',
              defaultMessage: 'Country',
            })} *`}
            id="country"
            name="country"
            FormControlProps={{ fullWidth: true, size: 'small' }}
            required
          />

          <Field
            component={TextFieldForm}
            props={{
              label: formatMessage({
                id: 'forms.common.registration.label',
                defaultMessage: 'Company Registration Number',
              }),
              slotProps: {
                htmlInput: {
                  maxLength: 64,
                },
              },
              size: 'small',
              fullWidth: true,
              withTrim: true,
            }}
            required={isRequiredExtraData}
            name="registration_number"
            data-element={elements.configuration.general.financial.registrationNumber}
          />

          <Field
            component={TextFieldForm}
            props={{
              label: formatMessage({
                id: 'forms.common.vat.label',
                defaultMessage: 'VAT number',
              }),
              slotProps: {
                htmlInput: {
                  maxLength: 64,
                },
              },
              size: 'small',
              fullWidth: true,
              withTrim: true,
            }}
            name="vat_number"
            required={isRequiredExtraData}
            data-element={elements.configuration.general.financial.vat}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button variant="text" onClick={onClose}>
          {formatMessage({
            id: 'button.cancel',
            defaultMessage: 'Cancel',
          })}
        </Button>
        <Button variant="contained" type="submit" loading={submitting} disabled={invalid}>
          {formatMessage({
            id: 'button.create',
            defaultMessage: 'Create',
          })}
        </Button>
      </DialogActions>
    </form>
  );
};

const mapStateToProps = (state) => ({
  picture: formValueSelector('createCompanyForm')(state, 'picture'),
  country: formValueSelector('createCompanyForm')(state, 'country'),
  initialValues: {
    country: organizationCountrySelector(state),
  },
});

export default compose(
  connect(mapStateToProps),
  reduxForm({
    form: 'createCompanyForm',
    validate: createCompanyValidate,
    touchOnChange: true,
  }),
)(CreateCompanyForm);
